import axios from 'axios';
import _ from 'lodash';
import {
  ArrowLeft,
  CircleHelp,
  Copy,
  FileSpreadsheet,
  FileText,
  Link2,
  Mail,
  Phone,
  Printer,
  Send,
  X,
} from 'lucide-react';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import useCsvDownloader from 'use-csv-downloader';
import { setTranscriptView } from '@/actions/userInterface';
import { CustomerInfo } from '@/components/runner/customer/CustomerInfo';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { CustomButton } from '@/components/ui/custom/CustomButton';
import { IconButton } from '@/components/ui/custom/IconButton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePhoneMask } from '@/hooks/usePhoneMask';
import usePersistentOpen from '@/hooks/usePersistentOpen';
import { cn } from '@/lib/utils';
import { useGetCustomerByIdQuery, useGetCustomersQuery } from '@/slices/customerSlice';
import { buildCanonicalCustomerConversationPath } from '@/utils/customerConversationRoutes';
import formatPhoneForUser from '@/utils/formatPhoneForUser';
import { MASKED_PHONE } from '@/utils/maskPhone';
import copyTextToClipboard from '../../../../utils/copyTextToClipboard';
import getWebchatUrl from '../../../../utils/getWebchatUrl';
import TranscriptionSection from '../Chatfeed/TranscriptionSection';

import Attachments from './Attachments';
import { CustomerNotes } from './CustomerNotes';
import { CustomerPhotos } from '@/components/customers/CustomerPhotos';
import CustomerWork from './CustomerWork';
import { DetailsGroup, DetailsSectionHeader } from './DetailsSection';
import formatConversationCSV from './formatConversationCSV';
import formatConversationPrint, { withLoadedImages } from './formatConversationPrint';
import LocationTransferMenu from './LocationTransferMenu';
import MessageSequences from './MessageSequences';
import PaymentsInfo from './PaymentsInfo';
import PipelineItems from './PipelineItems';
import PrintableConversation, { PRINT_STYLES } from './PrintableConversation';
// import { Notes } from './Notes';
import Reminders from './Reminders';
import ScheduledMessageInfo from './ScheduledMessageInfo';
import UnsubscribeCustomer from './UnsubscribeCustomer';

const ConversationDetails = (props) => {
  // On phones the inbox shows the details as a full-screen pushed page that
  // supplies the header and back button, so this renders the page body only.
  const isPage = Boolean(props.isMobile);
  const dispatch = useDispatch();
  const { transcriptView } = useSelector((state) => state.userInterface.webchat);

  // Group conversations carry multiple customers; the sidebar shows one at a
  // time via the participant switcher so the customer-scoped sections
  // (pipeline, reminders, payments, ...) work unchanged.
  const conversationCustomers = props.activeConversation?.isGroup
    ? props.activeConversation?._customers || []
    : props.activeConversation?._customer
      ? [props.activeConversation._customer]
      : [];

  const [selectedParticipantId, setSelectedParticipantId] = useState(null);

  const selectedCustomer =
    conversationCustomers.find((c) => c?._id === selectedParticipantId) ||
    conversationCustomers[0] ||
    null;

  const { customer, isSuccess: isSuccessUser } = useGetCustomersQuery('getCustomers', {
    skip: !selectedCustomer?._id,
    selectFromResult: ({ data, isLoading, isSuccess, isError, error }) => ({
      customer: data?.entities[selectedCustomer?._id],
      isLoading,
      isSuccess,
      isError,
      error,
    }),
  });

  const { data: customerDetails, isSuccess } = useGetCustomerByIdQuery(selectedCustomer?._id, {
    skip: !selectedCustomer?._id,
  });

  // The active conversation already has the basic identity; enrich it when the
  // customer query resolves instead of blanking the header between conversations.
  let customerInfo = selectedCustomer;

  if (isSuccessUser) {
    customerInfo = customer || customerInfo;
  }

  if (isSuccess) {
    customerInfo = customerDetails.entities[selectedCustomer?._id] || customerInfo;
  }

  // Conversation-embedded customers often lack _business; without it the edit
  // dialog saves against the user's active location and can't find the customer.
  const rawConversationBusiness = props.activeConversation?._business;
  const conversationBusiness =
    typeof rawConversationBusiness === 'object' ? rawConversationBusiness?._id : rawConversationBusiness;
  const editableCustomerInfo = useMemo(
    () => customerInfo && { ...customerInfo, _business: customerInfo._business || conversationBusiness },
    [customerInfo, conversationBusiness],
  );

  // Reset transcript view + participant selection when the active conversation
  // changes. Keyed on the conversation id (not transcriptView) so opening a
  // transcript within the current conversation doesn't immediately close it.
  useEffect(() => {
    dispatch(setTranscriptView(null));
    setSelectedParticipantId(null);
  }, [dispatch, props.activeConversation?._id]);

  if (!props.activeConversation || !selectedCustomer) {
    return null;
  }

  const handleCloseTranscript = () => {
    dispatch(setTranscriptView(null));
  };

  if (transcriptView) {
    return (
      <div
        className={cn(
          'flex flex-col h-full border-l bg-background transition-all duration-300 ease-in-out font-sans overflow-hidden',
          isPage
            ? 'min-h-full w-full border-l-0'
            : Object.keys(props.activeConversation).length !== 0 && props.showConversationDetails
              ? 'w-1/3'
              : 'w-0',
        )}
      >
        <div
          className={cn(
            'p-4 border-b flex items-center gap-2 bg-background',
            isPage && 'min-h-12 px-2 py-1',
          )}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCloseTranscript}
            aria-label="Back to details"
            className={cn('p-0 w-8 h-8', isPage && 'size-11 rounded-full')}
          >
            <ArrowLeft className={cn('h-4 w-4', isPage && 'size-5')} />
          </Button>
          <span className="font-medium">{isPage ? 'Call transcript' : 'Back to details'}</span>
        </div>
        <div className={cn('flex-1 overflow-y-auto min-h-0', isPage && 'pb-safe')}>
          <TranscriptionSection
            transcription={transcriptView.transcription}
            transcriptionStatus={transcriptView.transcriptionStatus}
            isVoicemail={transcriptView.isVoicemail}
            agentName={transcriptView.agentName}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        isPage
          ? 'flex min-h-full w-full min-w-0 flex-col bg-muted/40 pb-8 font-sans dark:bg-background [&>*]:shrink-0'
          : [
              'conversation-enter flex flex-col h-full min-w-0 max-h-[calc(100dvh-17px)] border-l border-border/60 font-sans overflow-y-auto scrollbar-none [&>*]:shrink-0',
              'bg-background',
              Object.keys(props.activeConversation).length !== 0 && props.showConversationDetails
                ? 'xl:w-[280px] xl:shrink-0 xl:max-w-none w-[85%] max-w-[350px] absolute right-0 h-full z-50 shadow-xl xl:static xl:shadow-none'
                : 'w-0 hidden border-none',
            ],
      )}
      key={props.activeConversation._id}
    >
      <div
        className={cn(
          'relative',
          isPage &&
            'mx-4 mt-3 overflow-hidden rounded-xl bg-background ring-1 ring-border/70 dark:bg-zinc-900 dark:ring-white/10 [&>*:last-child]:border-b-0',
        )}
      >
        {props.activeConversation.isGroup && conversationCustomers.length > 1 && (
          <div
            role={isPage ? 'group' : undefined}
            aria-label={isPage ? 'Show details for' : undefined}
            className={cn(
              'flex flex-wrap gap-1 px-3 pt-3',
              isPage && 'flex-nowrap gap-2 overflow-x-auto px-4 scrollbar-none',
            )}
          >
            {conversationCustomers.map((participant) => (
              <button
                key={participant._id}
                type="button"
                aria-pressed={isPage ? participant._id === selectedCustomer._id : undefined}
                onClick={() => setSelectedParticipantId(participant._id)}
                className={cn(
                  'px-2.5 py-1 rounded-full text-xs border transition-colors',
                  isPage && 'h-9 max-w-[70%] shrink-0 truncate px-3.5 py-0 text-sm',
                  participant._id === selectedCustomer._id
                    ? 'bg-primary text-primary-foreground border-transparent'
                    : 'bg-background text-muted-foreground hover:text-foreground',
                )}
              >
                {participant.name || participant.phone}
              </button>
            ))}
          </div>
        )}
        <CustomerInfo
          isLoading={!customerInfo}
          isLoadingDetails={!customer && !customerDetails?.entities[selectedCustomer._id]}
          customerInfo={editableCustomerInfo}
          isInSidebar
        />
        {isPage ? (
          <ContactActions customer={customerInfo} />
        ) : (
          <div className="absolute top-2 right-2">
            <IconButton
              variant="ghost"
              onClick={props.toggleShowConversationDetails}
              toolTipTitle="Close Conversation Details"
              aria-label="Close Conversation Details"
              className="shrink-0 text-muted-foreground hover:bg-transparent h-8 w-8"
            >
              <X className="size-4" />
            </IconButton>
          </div>
        )}
      </div>

      {/* these need to be collapsible; all scoped to the selected participant */}
      <DetailsGroup isPage={isPage}>
        <CustomerNotes
          key={`notes-${selectedCustomer._id}`}
          customerInfo={{
            _id: selectedCustomer._id,
            name: selectedCustomer.name,
            _business: props.activeConversation._business,
          }}
        />
      </DetailsGroup>
      <DetailsGroup isPage={isPage}>
        <PipelineItems _customer={selectedCustomer._id} key={`pipeline-${selectedCustomer._id}`} />
        <ScheduledMessageInfo
          key={`scheduled-${selectedCustomer._id}`}
          customer={selectedCustomer}
          refreshKey={props.scheduledMessagesRefreshKey}
          onChanged={props.refreshScheduledMessages}
          onResolved={props.onScheduledMessageResolved}
        />
        <MessageSequences
          key={`sequences-${selectedCustomer._id}`}
          _customer={selectedCustomer._id}
        />

        <Reminders _customer={selectedCustomer} key={`reminders-${selectedCustomer._id}`} />
      </DetailsGroup>
      <DetailsGroup isPage={isPage}>
        <Attachments
          messages={props.activeConversation.messages}
          _activeConversation={props.activeConversation._id}
        />
        <CustomerPhotos
          variant="section"
          key={`photos-${selectedCustomer._id}`}
          _customer={selectedCustomer._id}
          _business={props.activeConversation._business}
          customerName={selectedCustomer.name}
        />
        <PaymentsInfo customer={selectedCustomer} key={`payments-${selectedCustomer._id}`} />

        <SharedLinks messages={props.activeConversation.messages} />

        <CustomerWork _customer={selectedCustomer._id} key={`work-${selectedCustomer._id}`} />
      </DetailsGroup>
      <DetailsGroup isPage={isPage}>
        {!props.activeConversation.isGroup && (
          <UnsubscribeCustomer
            activeConversation={props.activeConversation}
            addConversationToCustomerConversations={props.addConversationToCustomerConversations}
          />
        )}

        <LocationTransferMenu activeConversation={props.activeConversation} />
        <ExportConversation activeConversation={props.activeConversation} isPage={isPage} />
      </DetailsGroup>
    </div>
  );
};

/**
 * Phone only: call or email the customer from this device, like the buttons
 * on an iOS contact card. A masked number (deactivated account) is not offered.
 */
const ContactActions = ({ customer }) => {
  const maskPhone = usePhoneMask();
  const phone =
    customer?.phone && maskPhone(customer.phone) !== MASKED_PHONE ? customer.phone : null;
  const email = customer?.email || null;

  if (!phone && !email) {
    return null;
  }

  const tileClass =
    'flex min-h-14 min-w-0 flex-col items-center justify-center gap-1 rounded-xl bg-muted/70 px-2 py-2 text-[13px] font-medium text-blue-600 transition-colors active:bg-muted dark:bg-zinc-800 dark:text-blue-400 dark:active:bg-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <div className="grid auto-cols-fr grid-flow-col gap-2 border-t border-border/60 px-4 py-3">
      {phone && (
        <a
          href={`tel:${String(phone).replace(/[^\d+]/g, '')}`}
          aria-label={`Call ${formatPhoneForUser(phone) || phone} from this device`}
          className={tileClass}
        >
          <Phone className="size-5" aria-hidden="true" />
          Call
        </a>
      )}
      {email && (
        <a href={`mailto:${email}`} aria-label={`Email ${email}`} className={tileClass}>
          <Mail className="size-5" aria-hidden="true" />
          Email
        </a>
      )}
    </div>
  );
};

const SharedLinks = (props) => {
  const [expanded, setExpanded] = useState(false);
  const [isOpen, setIsOpen] = usePersistentOpen('conversation-details:shared-links');

  if (!props.messages?.length) {
    return null;
  }

  const { messages } = props;

  const texts = messages.map((message) => message.text || '');

  const uniqueTexts = _.uniq(texts);

  const re = /(https?:\/\/[^\s]+)/g;

  const allUrls = [];
  for (const text of uniqueTexts) {
    // if text includes @customer-response, skip it
    if (text.includes('@customer-response?_')) {
      continue;
    }

    const parts = text.split(re);

    for (let i = 1; i < parts.length; i += 2) {
      allUrls.push(
        <a
          key={text}
          href={parts[i]}
          className="text-xs text-[#2986c6] hover:text-sky-700 max-md:flex max-md:min-h-11 max-md:items-center max-md:border-b max-md:border-border/50 max-md:text-sm max-md:last-of-type:border-b-0"
          style={{
            letterSpacing: '0.03em',
            wordBreak: 'break-word',
          }}
          target="_blank"
          rel="noopener noreferrer"
        >
          {parts[i].length > 60 ? parts[i].slice(0, 60).concat('...') : parts[i]}
        </a>,
      );
    }
  }

  if (!allUrls.length) {
    return null;
  }

  const visibleUrls = expanded ? allUrls : allUrls.slice(0, 5);

  return (
    <div className="border-b border-border/60">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <DetailsSectionHeader title="Shared links" />
        <CollapsibleContent>
          <div className="px-4 pb-3 space-y-1.5 flex flex-col max-md:space-y-0">
            {visibleUrls}
            {allUrls.length > 5 ? (
              <CustomButton
                id="expand-button"
                variant="outline"
                onClick={() => setExpanded(!expanded)}
                className="max-md:mt-2 max-md:min-h-11"
              >
                {expanded ? 'Collapse' : 'Expand'}
              </CustomButton>
            ) : (
              <></>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

const ExportConversation = (props) => {
  const auth = useSelector((state) => state.auth);
  const defaultButtonText = 'Copy link to conversation';

  const defaultCopyEmailText = 'Copy chekkit email';

  const [buttonText, setButtonText] = useState(defaultButtonText);
  const [copyEmailText, setCopyEmailText] = useState(defaultCopyEmailText);

  const [isSendingEmailToDealerRater, setIsSendingEmailToDealerRater] = useState(false);

  const [dealerRaterButtonText, setDealerRaterButtonText] = useState('Export to Dealer CRM');
  const [isOpenExport, setIsOpenExport] = usePersistentOpen(
    'conversation-details:export-conversation',
  );

  const users = useSelector((state) => state.users);
  const messageAutomations = useSelector((state) => state.messageAutomations);
  const printContentRef = useRef(null);
  const [printRecord, setPrintRecord] = useState(null);
  // Which button ('pdf' or 'print') started the print that is being prepared.
  const [preparingPrint, setPreparingPrint] = useState(null);

  // Prints a dedicated record (every message, not just the bubbles loaded in
  // the feed) in an isolated iframe, so the page breaks and colours don't
  // depend on the dashboard layout or theme. "Download PDF" and "Print" both
  // open it in the browser's print dialog.
  const printConversation = useReactToPrint({
    contentRef: printContentRef,
    documentTitle: `${
      props.activeConversation?.title || props.activeConversation?._customer?.name || 'Group'
    } Conversation - ${moment().format('YYYY-MM-DD')}`,
    ignoreGlobalStyles: true,
    pageStyle: PRINT_STYLES,
    onBeforePrint: async () => {
      try {
        const record = await withLoadedImages(
          formatConversationPrint({
            activeConversation: props.activeConversation,
            users,
            messageAutomations,
            businessName: auth.name,
            twilioNumber: auth.twilioNumber,
          }),
        );
        // The record has to be in the DOM before react-to-print copies it.
        flushSync(() => setPrintRecord(record));
      } finally {
        setPreparingPrint(null);
      }
    },
    onAfterPrint: () => setPrintRecord(null),
  });

  const startPrint = (source) => {
    setPreparingPrint(source);
    printConversation();
  };

  if (!props.activeConversation?._customer) {
    return null;
  }

  const link = `https://${window.location.host}${buildCanonicalCustomerConversationPath({
    _customerConversation: props.activeConversation._id,
    _business: props.activeConversation._business,
  })}`;

  const copyLink = () => {
    copyTextToClipboard(link);
    setButtonText('Copied');
    setTimeout(() => setButtonText(defaultButtonText), 2000);
  };

  const copyEmail = () => {
    const email = `hello.${props.activeConversation._business}.${props.activeConversation._customer._id}@reply.chekkit.io`;
    copyTextToClipboard(email);
    setCopyEmailText('Copied');
    setTimeout(() => setCopyEmailText(defaultCopyEmailText), 2000);
  };

  const exportCSV = () => {
    const filename = `${props.activeConversation.title}-${moment().format('l')}.csv`;
    const downloadCsv = useCsvDownloader();
    const formattedMessages = formatConversationCSV({
      messages: props.activeConversation.messages,
      users: props.users,
      customer: props.activeConversation._customer,
    });

    downloadCsv(formattedMessages, filename);
  };

  const exportToDealerRater = async () => {
    setIsSendingEmailToDealerRater(true);
    setDealerRaterButtonText('Sending to your CRM...');

    await axios.post(`${getWebchatUrl(window.location.href)}/api/export-to-dealerrater`, {
      _customerConversation: props.activeConversation._id,
    });

    setDealerRaterButtonText('Sent to your CRM');
  };

  const renderExportServices = () => {
    if (!auth.exportServices) {
      return null;
    }

    // // render button for various export services
    if (auth.exportServices.dealerSocket) {
      return (
        <CustomButton
          size="sm"
          onClick={exportToDealerRater}
          variant="outline"
          disabled={isSendingEmailToDealerRater}
        >
          {dealerRaterButtonText}
        </CustomButton>
      );
    }
  };

  const csvTitle = 'Download CSV';
  const pdfTitle = 'Download PDF';

  // Phone page: one full-width row per action, with the email explained inline
  // because a tooltip can't be hovered on a touch screen.
  const renderPageActions = () => {
    const rowClass =
      'flex min-h-12 w-full items-center gap-3 border-b border-border/50 py-2 text-left text-base text-foreground transition-colors last:border-b-0 active:bg-muted/60 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring';
    const iconClass = 'size-5 shrink-0 text-muted-foreground';

    return (
      <div className="flex flex-col px-4 pb-2">
        <button type="button" className={rowClass} onClick={exportCSV}>
          <FileSpreadsheet className={iconClass} aria-hidden="true" />
          {csvTitle}
        </button>
        <button
          type="button"
          className={rowClass}
          onClick={() => startPrint('pdf')}
          disabled={Boolean(preparingPrint)}
        >
          <FileText className={iconClass} aria-hidden="true" />
          {preparingPrint === 'pdf' ? 'Preparing…' : pdfTitle}
        </button>
        <button
          type="button"
          className={rowClass}
          onClick={() => startPrint('print')}
          disabled={Boolean(preparingPrint)}
        >
          <Printer className={iconClass} aria-hidden="true" />
          {preparingPrint === 'print' ? 'Preparing…' : 'Print'}
        </button>
        <button type="button" className={rowClass} onClick={copyLink} aria-live="polite">
          <Link2 className={iconClass} aria-hidden="true" />
          {buttonText}
        </button>
        {!auth.reseller && (
          <div className="border-b border-border/50 last:border-b-0">
            <button
              type="button"
              className={cn(rowClass, 'border-b-0')}
              onClick={copyEmail}
              aria-live="polite"
              aria-describedby="chekkit-email-help"
            >
              <Copy className={iconClass} aria-hidden="true" />
              {copyEmailText}
            </button>
            <p id="chekkit-email-help" className="pb-3 pl-8 text-[13px] leading-snug text-muted-foreground">
              Every conversation has its own email address. Anything emailed to it shows up here as
              a new message.
            </p>
          </div>
        )}
        {auth.exportServices?.dealerSocket && (
          <button
            type="button"
            className={rowClass}
            onClick={exportToDealerRater}
            disabled={isSendingEmailToDealerRater}
          >
            <Send className={iconClass} aria-hidden="true" />
            {dealerRaterButtonText}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="border-b border-border/60">
      <Collapsible open={isOpenExport} onOpenChange={setIsOpenExport}>
        <DetailsSectionHeader title="Export conversation data" />
        <CollapsibleContent>
          {props.isPage ? (
            renderPageActions()
          ) : (
          <div className="px-4 pb-3 flex flex-col items-start space-y-1.5">
            <CustomButton size="sm" onClick={exportCSV} variant="outline">
              <p className="lg:flex md:hidden">{csvTitle}</p>
              <p className="md:flex lg:hidden max-md:hidden">{csvTitle.split(' ')[1]}</p>
            </CustomButton>
            <CustomButton
              size="sm"
              onClick={() => startPrint('pdf')}
              variant="outline"
              disabled={Boolean(preparingPrint)}
            >
              {preparingPrint === 'pdf' ? (
                <p>Preparing…</p>
              ) : (
                <>
                  <p className="lg:flex md:hidden">{pdfTitle}</p>
                  <p className="md:flex lg:hidden max-md:hidden">{pdfTitle.split(' ')[1]}</p>
                </>
              )}
            </CustomButton>
            <CustomButton
              size="sm"
              onClick={() => startPrint('print')}
              variant="outline"
              disabled={Boolean(preparingPrint)}
            >
              <p>{preparingPrint === 'print' ? 'Preparing…' : 'Print'}</p>
            </CustomButton>
            <CustomButton size="sm" onClick={copyLink} variant="outline">
              <p className="text-wrap break-words">{buttonText}</p>
            </CustomButton>
            {!auth.reseller && (
              <div className="flex items-center gap-1.5">
                <CustomButton size="sm" onClick={copyEmail} variant="outline">
                  <p className="text-wrap break-words">{copyEmailText}</p>
                </CustomButton>
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label="What is the Chekkit email?"
                        className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                        <CircleHelp className="size-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent collisionPadding={8} className="max-w-64 leading-relaxed">
                      Every conversation has its own email address. Anything emailed to it shows up
                      here as a new message. Forward an email to it to save it in this conversation,
                      or give it to your customer so their emails land here.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
            {renderExportServices()}
          </div>
          )}
        </CollapsibleContent>
      </Collapsible>
      {/* Print source only: the inner node is cloned into the print iframe. */}
      <div style={{ display: 'none' }} aria-hidden="true">
        {printRecord && (
          <PrintableConversation
            contentRef={printContentRef}
            record={printRecord}
            printedBy={auth.user?.name || auth.user?.username}
          />
        )}
      </div>
    </div>
  );
};

export default ConversationDetails;
